import { useIntl } from 'react-intl';

import { BulbIcon } from '@/components/icons/bulb';
import { ExclamationIcon } from '@/components/icons/exclamation';
import { LightningBoltIcon } from '@/components/icons/lightning-bolt';

export type RecommendationType = 'SOURCING' | 'AUTOMATIONS' | 'AT_RISK';

export const useRecommendationContent = () => {
  const intl = useIntl();

  const getRecommendationContent = (
    type: RecommendationType,
    value?: number,
  ) => {
    switch (type) {
      case 'AT_RISK': {
        return {
          icon: ExclamationIcon,
          iconColor: 'text-fx-rose',
          headerColor: 'bg-fx-rose-50',
          headerText: intl.formatMessage({
            defaultMessage: 'Openings at risk',
            description:
              'Dashboard recommendation card ribbon: openings behind hiring goals',
          }),
          title: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# opening needs attention} other {# openings need attention}}',
              description:
                'Headline count of openings flagged as at risk (plural rules)',
            },
            { count: value ?? 0 },
          ),
          description: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {Hiring goal is at risk for # opening} other {Hiring goals are at risk for # openings}}',
              description:
                'One-line summary for the at-risk insight card body text, with count of affected openings',
            },
            { count: value ?? 0 },
          ),
          buttonText: intl.formatMessage({
            defaultMessage: 'Open Action Center',
            description:
              'CTA opening the side panel to review at-risk openings and fixes',
          }),
        };
      }
      case 'SOURCING': {
        return {
          icon: BulbIcon,
          iconColor: 'text-fx-blue',
          headerColor: 'bg-fx-blue-50',
          headerText: intl.formatMessage({
            defaultMessage: 'Start Sourcing',
            description:
              'Card section header: prompts user to set up job board campaigns',
          }),
          title: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {# live opening with no active campaign} other {# live openings with no active campaigns}}',
              description:
                'Headline: count of active openings with no campaigns (plural form)',
            },
            { count: value ?? 0 },
          ),
          description: intl.formatMessage(
            {
              defaultMessage:
                '{count, plural, one {This opening is active but has no campaign running} other {These openings are active but have no campaigns running}}',
              description:
                'Supporting sentence under the count: why this recommendation appears',
            },
            { count: value ?? 0 },
          ),
          buttonText: intl.formatMessage({
            defaultMessage: 'Set up sourcing',
            description:
              'Primary CTA on the sourcing insight card to configure campaigns',
          }),
        };
      }
      case 'AUTOMATIONS': {
        return {
          icon: LightningBoltIcon,
          iconColor: 'text-fx-blue',
          headerColor: 'bg-fx-blue-50',
          headerText: intl.formatMessage({
            defaultMessage: 'Automation Recommendations',
            description:
              'Card section header: automation cost-saving opportunities',
          }),
          title: intl.formatMessage({
            defaultMessage: 'Save money automatically',
            description:
              'Value proposition line for budget automation on the card',
          }),
          description: intl.formatMessage({
            defaultMessage:
              'Automations pause campaigns when goals, budgets, or timelines are met.',
            description:
              'One-line explainer of what automations do for spend control',
          }),
          buttonText: intl.formatMessage({
            defaultMessage: 'Enable automations',
            description: 'CTA to turn on or manage sourcing automation rules',
          }),
        };
      }
      default: {
        return null;
      }
    }
  };

  return { getRecommendationContent };
};
